import { Button, Checkbox, FormControlLabel, Stack, TextField, Typography } from "@mui/material"
import { doc, getDoc, setDoc } from "firebase/firestore"
import { useEffect, useState } from "react"

import { db } from "../../firebase"
import { PopupService } from "react-ui-components/components/PopupService"

const PATH = "app-configurations/adsConfig"

type AdsConfig = {
  maxWidth: string
  maxHeight: string
  showAdOnHome: boolean
  disabled: boolean
  adsLocation: string
  stickyAds: boolean
  adMaxTime: number
  borderStyle: string
}

const DEFAULT_CONFIG: AdsConfig = {
  maxWidth: "100%",
  maxHeight: "25%",
  showAdOnHome: true,
  disabled: false,
  adsLocation: "TOP",
  stickyAds: false,
  adMaxTime: 6,
  borderStyle: '{ borderColor: "#ddd", borderWidth: 1}',
}

/**
 * Bespoke single-doc editor for app-configurations/adsConfig, mirroring
 * BusinessAdsConfig.tsx's role as the carousel display-config editor
 * alongside the GenericList-driven ads ListComponent.
 */
export const AdsConfigComponent = () => {
  const [config, setConfig] = useState<AdsConfig>(DEFAULT_CONFIG)
  const [loading, setLoading] = useState(false)

  const load = async () => {
    const snap = await getDoc(doc(db, PATH))
    if (snap.exists()) {
      setConfig({ ...DEFAULT_CONFIG, ...(snap.data() as AdsConfig) })
    }
  }

  useEffect(() => {
    load()
  }, [])

  const handleSave = async () => {
    setLoading(true)
    try {
      await setDoc(doc(db, PATH), config, { merge: true })
      PopupService.pushPopup("Ads configuration saved.")
    } catch (error: any) {
      PopupService.pushPopup(`Failed to save ads configuration: ${error.message}`)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Stack spacing={2} sx={{ maxWidth: 480, mb: 4 }}>
      <Typography variant="h6">Ads Display Config</Typography>
      <TextField
        label="Max Width"
        value={config.maxWidth}
        onChange={(e) => setConfig((c) => ({ ...c, maxWidth: e.target.value }))}
      />
      <TextField
        label="Max Height"
        value={config.maxHeight}
        onChange={(e) => setConfig((c) => ({ ...c, maxHeight: e.target.value }))}
      />
      <TextField
        label="Ads Location"
        value={config.adsLocation}
        onChange={(e) => setConfig((c) => ({ ...c, adsLocation: e.target.value }))}
      />
      <TextField
        label="Ad Max Time (seconds)"
        type="number"
        value={config.adMaxTime}
        onChange={(e) => setConfig((c) => ({ ...c, adMaxTime: Number(e.target.value) }))}
      />
      <TextField
        label="Border Style (JS object literal)"
        value={config.borderStyle}
        onChange={(e) => setConfig((c) => ({ ...c, borderStyle: e.target.value }))}
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={config.showAdOnHome}
            onChange={(e) => setConfig((c) => ({ ...c, showAdOnHome: e.target.checked }))}
          />
        }
        label="Show Ad On Home"
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={config.stickyAds}
            onChange={(e) => setConfig((c) => ({ ...c, stickyAds: e.target.checked }))}
          />
        }
        label="Sticky Ads"
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={config.disabled}
            onChange={(e) => setConfig((c) => ({ ...c, disabled: e.target.checked }))}
          />
        }
        label="Disabled (kill switch for all ads)"
      />
      <Button variant="contained" onClick={handleSave} disabled={loading}>
        Save
      </Button>
    </Stack>
  )
}

export default AdsConfigComponent
